import { Box, Divider, IconButton, SelectChangeEvent, Tooltip } from '@mui/material';
import { Headings, LargeText, MediumText, NormalText } from 'components/atoms/Typography';
import CartCheckbox from 'components/CartCheckbox';
import { Timestamp } from 'config/firebase';
import { useEffectInspector } from 'hooks/useEffectInspector';
import { useRest } from 'hooks/useRest';
import { Cart, Restaurant } from 'klikni-jadi-shared-stuff';
import { forEach, has, isEmpty, isEqual, keys, values } from 'lodash';
import moment, { Moment } from 'moment';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useRef, useState } from 'react';
import { _generateToken } from 'services/cart/useCartService/token';
import PreorderPicker from './components/PreorderPicker';
import { collection } from 'services/db/firestoreApi';
import { useCartService } from 'services/cart/useCartService';
import InfoIcon from '@mui/icons-material/Info';

interface IWhenCartProps {
    cart: Cart;
}

export interface PreorderData {
    days: Moment[];
    allResults: Record<string, Moment[]>;
    onlyPreorderAvailable: boolean;
}

const WhenCart = ({ cart }: IWhenCartProps) => {
    const { t } = useTranslation('common');
    const { addPreorder } = useCartService();
    const { propertiesAvailability } = useRest();

    const [isTimePickerInitialized, setIsTimePickerInitialized] = useState<boolean>(false);
    const [isPreorderSelected, setIsPreorderSelected] = useState<boolean>(false);
    const [dateSelected, setDateSelected] = useState<Moment>(null);
    const [timeSelected, setTimeSelected] = useState<Moment>(null);
    const [preorderData, setPreorderData] = useState<PreorderData>(null);

    // const [days, setDays] = useState<Moment[]>([]);
    // const [allResults, setAllResults] = useState<Record<string, Moment[]>>({});

    // const [isDefaultPreorderDateSet, setIsDefaultPreorderDateSet] = useState<boolean>(false);
    // const [onlyPreorderAvailable, setOnlyPreorderAvailable] = useState<boolean>(null);
    // const [areAllRestaurantsOpenSet, setAreAllRestaurantsOpenSet] = useState<boolean>(false);

    /**
     * This effect gets data from propertiesAvailability, then sets the values for {@link days}, {@link allResults}.
     * allResults = {"17-06-2023": [Moment(1000), Moment(1030), ..], ..}
     * dates = [Moment(2023-06-17), Moment(2023-06-18), ..]
     */
    useEffect(() => {
        console.log('cart?.restaurantIds', cart?.restaurantIds);
        if (cart?.restaurantIds) {
            _generateToken().then(_token => {
                propertiesAvailability
                    .getOne(cart.restaurantIds?.join(','), null, { 'x-access-token': _token }, 'availableHours')
                    .then(res => {
                        const _dates = values(res?.data?.results).map(item => moment(item.date));
                        const _allResults = values(res?.data?.results).reduce(
                            (acc, item) => ({
                                ...acc,
                                [moment(item.date).format('DD-MM-YYYY')]: item.times.map(ts => moment(ts, 'HHmm')),
                            }),
                            {}
                        );
                        setPreorderData({
                            days: _dates,
                            allResults: _allResults,
                            onlyPreorderAvailable: res?.data?.onlyPreorderAvailable,
                        });
                    });
            });
        }
    }, [cart?.restaurantIds]);

    /**
     * This initializes data for PreorderPicker.
     */
    useEffect(() => {
        if (!isTimePickerInitialized && !isEmpty(preorderData?.allResults)) {
            initializeTimePicker();
        }
    }, [preorderData]);

    /**
     * If asPreorder is true: initializes date and time from cart.
     * If asPreorder is false: initializes date and time with the first available date and time.
     */
    const initializeTimePicker = () => {
        let date, time;
        let isPreorderdateInvalid = false; // preorderdate can be invalid if the cart was created as preorder a day/s before today.

        if (cart?.isPreorder) {
            setIsPreorderSelected(true);
            date = moment(cart.deliveryDate.toDate());
            const checkDay = date.format('DD-MM-YYYY');
            const checkTime = date.format('HH:ss');
            if (
                keys(preorderData?.allResults).includes(checkDay) &&
                preorderData.allResults[checkDay].find(moment => moment.format('HH:ss') == checkTime)
            ) {
                console.log('VALID');
                time = moment(cart.deliveryDate.toDate());
            } else {
                console.log('INVALID');
                isPreorderdateInvalid = true;
            }
        }
        if (!cart?.isPreorder || isPreorderdateInvalid) {
            date = preorderData?.days[0];
            time = preorderData?.allResults[date.format('DD-MM-YYYY')][0];
            setIsPreorderSelected(preorderData?.onlyPreorderAvailable);
        }
        setDateSelected(date);
        setTimeSelected(time);
        setIsTimePickerInitialized(true);
    };

    useEffect(() => {
        if (timeSelected) {
            updatePreorderInCart(isPreorderSelected);
        }
    }, [timeSelected]);

    const updatePreorderInCart = (isPreorder: boolean) => {
        if (isPreorder) {
            const date = moment(
                `${dateSelected?.format('DD MMMM YYYY')} ${timeSelected.format('HH:mm')}`,
                'DD MMMM YYYY HH:mm'
            ).toDate();
            console.log('LNDate', date.toString());
            console.log('LNDate', date);
            addPreorder(date, true);
        } else {
            addPreorder(null, false);
        }
    };

    /**
     *
     * @param value Format: 'DD-MM-YYYY'. Example: '22-06-2023'
     */
    const handleDateSelect = (value: string) => {
        const date = moment(value, 'DD-MM-YYYY');
        const time = preorderData?.allResults[date.format('DD-MM-YYYY')][0].format('HH:mm');
        setDateSelected(date);
        // Set the first available time in the selected day.
        handleTimeSelect(time);
    };

    /**
     *
     * @param value Format: 'HH:mm'. Example: '15:00'
     */
    const handleTimeSelect = (value: string) => {
        const time = moment(value, 'HH:mm');
        setTimeSelected(time);
        setIsPreorderSelected(true);
    };

    console.log('LN', 'isPreorderSelected', isPreorderSelected);

    return (
        <Box pt={4} display="flex" flexDirection="column" width="100%">
            <Headings.H2 sx={{ mb: 3 }}>{t('when')}</Headings.H2>
            <Box pb={4} display="flex" alignItems="center" justifyContent="space-between">
                <Box
                    display="flex"
                    alignItems="center"
                    sx={
                        preorderData?.onlyPreorderAvailable
                            ? {
                                  opacity: 0.4,
                                  pointerEvents: 'none',
                              }
                            : {}
                    }
                >
                    <Box width="60px">
                        <CartCheckbox
                            checked={!isPreorderSelected}
                            handleCheck={() => {
                                setIsPreorderSelected(false);
                                updatePreorderInCart(false);
                            }}
                        />
                    </Box>
                    <Box display="flex" flexDirection="column">
                        <MediumText semibold>{t('asap')} </MediumText>
                    </Box>
                </Box>
                <Box>
                    {preorderData?.onlyPreorderAvailable && (
                        <Tooltip title="Некој од избраните ресторани не е отвороен во моментов.">
                            <IconButton>
                                <InfoIcon sx={{ color: 'primary.main' }} />
                                {/* #ffb818 */}
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </Box>
            {timeSelected && cart?.groupedItems && Object.keys(cart?.groupedItems)?.length >= 1 && (
                <Box pb={5} display="flex" alignItems="center">
                    <Box width="60px">
                        <CartCheckbox
                            checked={isPreorderSelected}
                            handleCheck={() => {
                                setIsPreorderSelected(true);
                                updatePreorderInCart(true);
                            }}
                        />
                    </Box>
                    <Box pb={1} display="flex" flexDirection="column">
                        <MediumText semibold>{t('schedule_later')} </MediumText>
                        <PreorderPicker
                            days={preorderData?.days}
                            times={preorderData?.allResults[dateSelected?.format('DD-MM-YYYY')] || []}
                            timeSelected={timeSelected}
                            dateSelected={dateSelected}
                            onTimeSelect={handleTimeSelect}
                            onDateSelect={handleDateSelect}
                        />
                    </Box>
                </Box>
            )}
            <Divider />
        </Box>
    );
};

export default WhenCart;
